import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, BadgeDollarSign, Ban, Receipt } from "lucide-react";
import { api } from "../../api/client";
import { PageHeaderBand } from "../../components/layout/PageHeaderBand";
import { LoadFailureNotice } from "../../components/ui/LoadFailureNotice";
import { useAuth } from "../../contexts/AuthContext";
import { useTenantTimeZone } from "../../hooks/useTenantTimeZone";
import { unwrapArray } from "../../lib/api-envelope";
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from "../../lib/load-failure";
import { displayValue } from "../../features/fieldiq-handoff/contracts";
import { DhEmpty, DhPanel } from "../downhole/DownholeBits";
import { DAMAGE_CHARGE_READ_ROLES, DAMAGE_CHARGE_WRITE_ROLES, DamageChargeRows } from "../../components/rentals/DamageCharges";
import type { RentalDamageCharge, RentalDamageChargeStatus } from "../../types/rental-damage-charge";

/**
 * Damage charges, /rentals/damage-charges (#815 G): every charge a return
 * found, opening on the "Needs pricing" queue. GET /surface-rentals/damage-
 * charges (unpaginated); the chips filter what was loaded. Each charge names
 * its order and links to it; admin and office staff price, waive or reopen.
 */

type Filter = RentalDamageChargeStatus | "all";
const FILTERS: { key: Filter; label: string }[] = [
  { key: "pending", label: "Needs pricing" },
  { key: "priced", label: "Priced" },
  { key: "waived", label: "Waived" },
  { key: "billed", label: "Billed" },
  { key: "all", label: "All" },
];

export default function RentalDamageCharges() {
  const navigate = useNavigate();
  const timeZone = useTenantTimeZone();
  const { user } = useAuth();
  const role = String((user as { role?: string } | null)?.role ?? "");
  const canRead = DAMAGE_CHARGE_READ_ROLES.includes(role);
  const canWrite = DAMAGE_CHARGE_WRITE_ROLES.includes(role);
  const [filter, setFilter] = useState<Filter>("pending");

  const query = useQuery({
    queryKey: ["rental-damage-charges", "all"],
    queryFn: async () => unwrapArray<RentalDamageCharge>((await api.rentals.damageCharges.list()).data),
    enabled: canRead,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const failed = queryLoadFailure(query) != null;
  const loading = canRead && query.isLoading && !failed;
  const all = useMemo(() => query.data ?? [], [query.data]);
  const count = (f: Filter) => (failed || !query.data ? null : all.filter((c) => f === "all" || c.status === f).length);
  const shown = all.filter((c) => filter === "all" || c.status === filter);

  return (
    <div data-testid="rental-damage-charges-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Surface rentals"
        title="Damage Charges"
        onBack={() => navigate(-1)}
        backLabel="Back"
        stats={
          canRead
            ? [
                { icon: <AlertTriangle className="size-4" aria-hidden="true" />, label: "Needs pricing", sub: "Found on return", tone: "accent", loading, value: displayValue(count("pending")) },
                { icon: <BadgeDollarSign className="size-4" aria-hidden="true" />, label: "Priced", sub: "Next invoice", tone: "info", loading, value: displayValue(count("priced")) },
                { icon: <Ban className="size-4" aria-hidden="true" />, label: "Waived", sub: "Bills nothing", tone: "neutral", loading, value: displayValue(count("waived")) },
                { icon: <Receipt className="size-4" aria-hidden="true" />, label: "Billed", sub: "On an invoice", tone: "success", loading, value: displayValue(count("billed")) },
              ]
            : undefined
        }
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {!canRead ? (
            <p className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-10 text-center text-sm text-fieldiq-text">
              Damage charges are visible to admin, office staff, supervisors and dispatchers.
            </p>
          ) : (
            <>
              <div className="scrollbar-hide flex gap-2 overflow-x-auto">
                {FILTERS.map((f) => {
                  const active = filter === f.key;
                  const n = count(f.key);
                  return (
                    <button
                      key={f.key}
                      type="button"
                      aria-pressed={active}
                      onClick={() => setFilter(f.key)}
                      className={`inline-flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-fi-pill px-4 py-1.5 text-xs font-extrabold transition ${
                        active ? "bg-fi-amber text-fi-amber-ink" : "border border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text"
                      }`}
                    >
                      {f.label}
                      {!loading && n != null && <span className="tabular-nums">{n}</span>}
                    </button>
                  );
                })}
              </div>
              <DhPanel eyebrow="Rentals" title="Damage found on return" testId="damage-charges-register">
                {failed ? (
                  <LoadFailureNotice error={queryFailureError(query)} subject="damage charges" />
                ) : loading ? (
                  <div className="p-4" aria-hidden="true"><div className="h-16 animate-pulse rounded-fi-control bg-fieldiq-dark/60" /></div>
                ) : shown.length === 0 ? (
                  <DhEmpty>{all.length === 0 ? "No damage charges yet." : filter === "pending" ? "Nothing needs pricing." : "None here."}</DhEmpty>
                ) : (
                  <DamageChargeRows charges={shown} canWrite={canWrite} showOrder timeZone={timeZone} />
                )}
              </DhPanel>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
